import { useEffect } from 'react';
import { useState, useMemo, useCallback } from 'react';

import { PopupStore } from '@gem/popups';

import Tasks from 'APP/Tasks';
import { ReactionsDetailedPopup } from 'APP/main/PopupManager/Popups/ReactionsDetailedPopup/ReactionsDetailedPopup';
import { useReactions } from 'APP/main/hooks/useReactions/useReactions';
import { IReactionItem } from 'APP/main/hooks/useReactions/useReactions.types';
import { Channel } from 'STORE/Groups/Channel';
import { P2P } from 'STORE/Groups/P2P';
import { Thread } from 'STORE/Groups/Thread';
import { ChatMessage } from 'STORE/Messages/Message/ChatMessage/ChatMessage';

interface IReactionsPresenterParams {
  message: ChatMessage;
  isVisible: boolean;
}

interface IReactionsPresenter {
  childPopup: PopupStore | null;
  myReactionId: string | null;
  animatedReactionId: string | null;
  isShowAnimations: boolean;
  isShow: boolean;
  reactions: IReactionItem[];
  myReaction: IReactionItem | null;
  canBeShowPopover: boolean;
  onOpenReactionPopup(reaction: string): void;
}

export const useReactionsPresenter = (data: IReactionsPresenterParams): IReactionsPresenter => {
  const { message, isVisible } = data;

  const reactions = useReactions(message);
  const [childPopup, setChildPopup] = useState<PopupStore | null>(null);
  const [myReactionId, setMyReactionId] = useState<string | null>(null);
  const [animatedReactionId, setAnimatedReactionId] = useState<string | null>(null);
  const [isShowAnimations, setIsShowAnimations] = useState<boolean>(false);

  const stableReactions = useMemo(() => reactions.reactionsMessage, [reactions.reactionsMessage]);

  const myReaction = useMemo(
    () => stableReactions.find((reaction) => reaction.hasMyReaction) || null,
    [stableReactions]
  );

  const canBeShowPopover = useMemo(() => {
    const group = message.group;

    if (!group || group.isFake) {
      return false;
    }

    if (group instanceof P2P) {
      return false;
    }

    if (group instanceof Channel && !group.withMeInAdmins) {
      return false;
    }

    if (group instanceof Thread) {
      if (group.parentGroup instanceof P2P) {
        return false;
      }

      if (group.parentGroup instanceof Channel && !group.parentGroup.withMeInAdmins) {
        return false;
      }
    }

    return true;
  }, [message.group]);

  const onOpenReactionPopup = useCallback(
    (reaction: string) => {
      const popup = Tasks.app.openPopup(ReactionsDetailedPopup, {
        params: {
          groupId: message.groupId,
          messageId: message.id,
          tab: reaction,
        },
      });
      setChildPopup(popup);
    },
    [message.groupId, message.id]
  );

  useEffect(() => {
    const newReactionId = myReaction?.id || null;
    if (myReactionId !== newReactionId) {
      setMyReactionId(newReactionId);
      setAnimatedReactionId(newReactionId);
    }
  }, [myReaction?.id, myReactionId]);

  useEffect(() => {
    if (isVisible) {
      setIsShowAnimations(true);
    }
  }, [isVisible]);

  useEffect(() => {
    return () => {
      if (childPopup) {
        childPopup.close();
      }
    };
  }, [childPopup, message.id]);

  return {
    childPopup,
    myReactionId,
    animatedReactionId,
    isShowAnimations,
    isShow: reactions.hasReactions,
    reactions: stableReactions,
    myReaction,
    canBeShowPopover,
    onOpenReactionPopup,
  };
};
